import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Clock, Zap, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function TrialBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const isOwner = user?.role === 'owner' && !user?.is_global_admin;

  const fetchOrg = useCallback(async () => {
    if (!isOwner) return;
    const token = localStorage.getItem('pulse_token');
    try {
      const res = await fetch(`${API}/api/organization`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();

      // Check if there's an active subscription — if so, don't show trial banner
      if (data.subscription?.status === 'active') {
        setSubscription(data.subscription);
        return;
      }

      const trialEnd = new Date(data.trial_ends_at);
      const now = new Date();
      const diff = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
      setDaysLeft(diff);
    } catch { /* silent */ }
  }, [isOwner]);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  // Don't render for non-owners, active subscribers, or if dismissed
  if (!isOwner || subscription || dismissed || daysLeft === null) return null;

  const expired = daysLeft <= 0;
  const urgent = daysLeft <= 7 && daysLeft > 0;

  const bgColor = expired
    ? 'bg-red-600'
    : urgent
      ? 'bg-amber-500'
      : 'bg-slate-700';

  const message = expired
    ? 'Your free trial has expired.'
    : daysLeft === 1
      ? '1 day left in your free trial.'
      : `${daysLeft} days left in your free trial.`;

  return (
    <div
      className={`${bgColor} text-white text-sm flex items-center justify-center gap-3 px-4 py-2 relative z-40`}
      data-testid="trial-banner"
    >
      <Clock className="h-4 w-4 shrink-0" />
      <span className="font-medium">{message}</span>
      <Button
        size="sm"
        variant="secondary"
        className="h-7 px-3 text-xs font-semibold"
        onClick={() => navigate('/pricing')}
        data-testid="trial-upgrade-btn"
      >
        <Zap className="h-3 w-3 mr-1" />
        Upgrade Now
      </Button>
      {!expired && (
        <button
          onClick={() => setDismissed(true)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
          aria-label="Dismiss"
          data-testid="trial-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
